import type {
  ConflictCheckStatus,
  CooperationMessage,
  CooperationRequest,
  CooperationRequestConfidentiality,
  CooperationRequestEvent,
  CooperationRequestEventType,
  CooperationRequestStatus,
  CooperationRequestType,
  CooperationUrgency,
  Firm,
  LexData,
  MembershipCategory,
  PracticeArea,
  User,
} from "./types";
import { hasTechnicalRole, isAdmin, isFirmRepresentative } from "./domain";

export const cooperationRequestTypes: { value: CooperationRequestType; label: string; description: string }[] = [
  { value: "ask_view", label: "Ask for a view", description: "A light request for an informal legal or practical view." },
  { value: "local_counsel", label: "Local counsel request", description: "A request for jurisdiction-specific local support." },
  { value: "co_counsel", label: "Co-counsel request", description: "A request to explore acting together or coordinating advice." },
  { value: "matter_referral", label: "Matter referral request", description: "A controlled request to explore a member-to-member referral without fee tracking." },
  { value: "expert_input", label: "Expert input request", description: "A targeted request for technical or specialist expertise." },
  { value: "jurisdiction_assistance", label: "Jurisdiction assistance", description: "A request for orientation in a specific jurisdiction." },
  { value: "working_group_cooperation", label: "Working group cooperation", description: "A request linked to a network project or working group." },
];

export const cooperationStatuses: { value: CooperationRequestStatus; label: string }[] = [
  { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" },
  { value: "awaiting_conflict_check", label: "Awaiting professional review" },
  { value: "conflict_check_requested", label: "Professional review requested" },
  { value: "conflict_cleared", label: "Professional review cleared" },
  { value: "conflict_declared", label: "Conflict declared" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "introduced", label: "Introduced" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
  { value: "expired", label: "Expired" },
  { value: "withdrawn", label: "Withdrawn" },
];

export const cooperationUrgencies: { value: CooperationUrgency; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "time_sensitive", label: "Time-sensitive" },
  { value: "urgent", label: "Urgent" },
];

export const cooperationConfidentialityLevels: { value: CooperationRequestConfidentiality; label: string }[] = [
  { value: "requester_and_target_only", label: "Requester and target only" },
  { value: "invited_participants_only", label: "Invited participants only" },
  { value: "committee_visible", label: "Committee-visible" },
  { value: "limited_summary", label: "Limited summary" },
  { value: "archived_private", label: "Archived private" },
];

export const conflictSafeWarning = "Do not include client names, confidential facts or privileged information at this stage unless you are sure it is appropriate.";
export const professionalResponsibilityNotice = "Do not disclose client names or confidential facts until appropriate. Each firm remains responsible for conflict checks and professional obligations.";

const conflictDefaultTypes: CooperationRequestType[] = [];
const activeStatuses: CooperationRequestStatus[] = ["sent", "viewed", "awaiting_conflict_check", "conflict_check_requested", "conflict_cleared", "accepted", "introduced", "in_progress"];
const completedStatuses: CooperationRequestStatus[] = ["completed", "closed", "declined", "conflict_declared", "expired", "withdrawn"];

export function labelForRequestType(type: CooperationRequestType) {
  return cooperationRequestTypes.find((item) => item.value === type)?.label ?? type;
}

export function labelForRequestStatus(status: CooperationRequestStatus) {
  return cooperationStatuses.find((item) => item.value === status)?.label ?? status;
}

export function defaultConflictCheckRequired(type: CooperationRequestType) {
  return conflictDefaultTypes.includes(type);
}

export function initialStatusForRequest(type: CooperationRequestType, conflictCheckRequired = defaultConflictCheckRequired(type)): CooperationRequestStatus {
  return conflictCheckRequired ? "awaiting_conflict_check" : "sent";
}

export function requestAffectsVotingOrParticipation() {
  return false;
}

export function containsProhibitedCommercialData(value: unknown): boolean {
  const text = typeof value === "string" ? value : JSON.stringify(value ?? "");
  return /\b(matter value|expected fee|expected fees|referral fee|success fee|legal fee|fees generated|revenue|invoice amount|client value)\b/i.test(text);
}

export function validateCooperationRequestDraft(input: Partial<CooperationRequest>) {
  if (!input.requestType) return "Choose a request type.";
  if (!input.title?.trim()) return "Add a short title.";
  if (!input.shortSummary?.trim()) return "Add a non-confidential summary.";
  if (!input.requesterFirmId) return "Requester firm is required.";
  if (!input.requesterUserId) return "Requester user is required.";
  if (!input.targetFirmId) return "Target firm is required.";
  if (input.requesterFirmId === input.targetFirmId) return "Requests cannot be sent within the same firm.";
  if (!input.confidentialityLevel) return "Choose a confidentiality level.";
  if (containsProhibitedCommercialData(input)) return "Do not include matter values, fees, revenue or referral-fee information.";
  return null;
}

export function createCooperationRequest(input: {
  requestType: CooperationRequestType;
  title: string;
  shortSummary: string;
  jurisdictionTags: string[];
  practiceAreaIds: string[];
  urgency: CooperationUrgency;
  confidentialityLevel: CooperationRequestConfidentiality;
  requesterFirmId: string;
  requesterUserId: string;
  targetFirmId: string;
  targetUserId?: string | null;
  relatedMemberId?: string | null;
  conflictCheckRequired?: boolean;
  allowFirmRouting?: boolean;
  deadline?: number | null;
  now?: number;
}): CooperationRequest {
  const createdAt = input.now ?? Date.now();
  const conflictCheckRequired = input.conflictCheckRequired ?? defaultConflictCheckRequired(input.requestType);
  const request: CooperationRequest = {
    id: `coop-req-${createdAt}`,
    requestType: input.requestType,
    title: input.title.trim(),
    shortSummary: input.shortSummary.trim(),
    jurisdictionTags: input.jurisdictionTags,
    practiceAreaIds: input.practiceAreaIds,
    urgency: input.urgency,
    confidentialityLevel: input.confidentialityLevel,
    requesterFirmId: input.requesterFirmId,
    requesterUserId: input.requesterUserId,
    targetFirmId: input.targetFirmId,
    targetUserId: input.targetUserId ?? null,
    relatedMemberId: input.relatedMemberId ?? null,
    status: initialStatusForRequest(input.requestType, conflictCheckRequired),
    conflictCheckRequired,
    clientDisclosed: false,
    clientNameEncryptedOrNull: null,
    confidentialDetailsAdded: false,
    allowFirmRouting: input.allowFirmRouting ?? true,
    deadline: input.deadline ?? null,
    invitedUserIds: [],
    createdAt,
    updatedAt: createdAt,
    expiresAt: input.deadline ?? null,
    closedAt: null,
    metadataJson: JSON.stringify({ source: "workspace-ui", governanceImpact: false }),
  };
  const error = validateCooperationRequestDraft(request);
  if (error) throw new Error(error);
  return request;
}

export function createCooperationRequestEvent(request: CooperationRequest, eventType: CooperationRequestEventType, actor: Pick<User, "id" | "firmId">, metadata?: Record<string, unknown>): CooperationRequestEvent {
  return {
    id: `coop-evt-${request.id}-${eventType}-${Date.now()}`,
    cooperationRequestId: request.id,
    eventType,
    actorUserId: actor.id,
    actorFirmId: actor.firmId ?? request.requesterFirmId,
    createdAt: Date.now(),
    metadataJson: metadata ? JSON.stringify(metadata) : undefined,
  };
}

export function transitionCooperationRequest(request: CooperationRequest, status: CooperationRequestStatus) {
  const closed = completedStatuses.includes(status);
  return {
    ...request,
    status,
    updatedAt: Date.now(),
    closedAt: closed ? Date.now() : request.closedAt ?? null,
  };
}

export function createCooperationMessage(input: {
  request: CooperationRequest;
  sender: Pick<User, "id" | "firmId">;
  body: string;
  containsConfidentialDetails?: boolean;
}): CooperationMessage {
  if (containsProhibitedCommercialData(input.body)) {
    throw new Error("Messages must not include matter values, fees, revenue or referral-fee information.");
  }
  return {
    id: `coop-msg-${Date.now()}`,
    cooperationRequestId: input.request.id,
    senderUserId: input.sender.id,
    senderFirmId: input.sender.firmId ?? input.request.requesterFirmId,
    messageBody: input.body.trim(),
    containsConfidentialDetails: input.containsConfidentialDetails ?? false,
    createdAt: Date.now(),
    editedAt: null,
  };
}

export function canCreateCooperationRequest(user: Pick<User, "role" | "active">, category: MembershipCategory, type: CooperationRequestType, invited = false) {
  if (!user.active) return false;
  if (category === "Observer") return false;
  if (category === "Full Member") return true;
  if (category === "Associate Member") return true;
  if (category === "Expert Contributor") return invited || type === "expert_input";
  return false;
}

export function canReceiveCooperationRequest(firm: Pick<Firm, "membershipCategory" | "acceptsCooperationRequests">, type: CooperationRequestType) {
  if (firm.acceptsCooperationRequests === false) return false;
  if (firm.membershipCategory === "Observer") return false;
  if (firm.membershipCategory === "Expert Contributor") return type === "expert_input" || type === "ask_view" || type === "working_group_cooperation";
  return true;
}

export function canViewCooperationRequest(user: User, request: CooperationRequest, metadataOnly = false) {
  if (!user.active) return false;
  if (user.firmId === request.requesterFirmId || user.firmId === request.targetFirmId) return true;
  if (request.invitedUserIds?.includes(user.id)) return true;
  if (request.confidentialityLevel === "committee_visible" && (isAdmin(user) || hasTechnicalRole(user, "workspace_manager") || user.role === "Coordination Committee" || user.role === "Cooperation Panel")) return true;
  return metadataOnly && isAdmin(user);
}

export function canMutateCooperationRequest(user: User, request: CooperationRequest) {
  if (!canViewCooperationRequest(user, request)) return false;
  if (isAdmin(user) && request.confidentialityLevel !== "requester_and_target_only") return true;
  return (user.firmId === request.requesterFirmId || user.firmId === request.targetFirmId) && isFirmRepresentative(user);
}

export function conflictStatusForRequest(request: CooperationRequest, status?: ConflictCheckStatus) {
  if (!request.conflictCheckRequired) return "Not required";
  if (status === "cleared" || request.status === "conflict_cleared") return "Cleared";
  if (status === "conflict_declared" || request.status === "conflict_declared") return "Conflict declared";
  if (status === "unable_to_assess") return "Unable to assess";
  if (status === "requested" || request.status === "conflict_check_requested" || request.status === "awaiting_conflict_check") return "Pending";
  return "Not requested";
}

export function safeNotificationPreview(request: CooperationRequest) {
  return `${labelForRequestType(request.requestType)} request: ${request.title}`;
}

export function filterCooperationRequests(
  requests: CooperationRequest[],
  options: {
    tab?: "inbox" | "sent" | "active" | "completed" | "archived";
    viewerFirmId?: string | null;
    requestType?: CooperationRequestType | "all";
    status?: CooperationRequestStatus | "all";
    jurisdiction?: string;
    practiceAreaId?: string;
    urgency?: CooperationUrgency | "all";
  },
) {
  return requests.filter((request) => {
    if (options.tab === "inbox" && request.targetFirmId !== options.viewerFirmId) return false;
    if (options.tab === "sent" && request.requesterFirmId !== options.viewerFirmId) return false;
    if (options.tab === "active" && !activeStatuses.includes(request.status)) return false;
    if (options.tab === "completed" && !completedStatuses.includes(request.status)) return false;
    if (options.tab === "archived" && request.status !== "closed") return false;
    if (options.requestType && options.requestType !== "all" && request.requestType !== options.requestType) return false;
    if (options.status && options.status !== "all" && request.status !== options.status) return false;
    if (options.jurisdiction && !request.jurisdictionTags.includes(options.jurisdiction)) return false;
    if (options.practiceAreaId && !request.practiceAreaIds.includes(options.practiceAreaId)) return false;
    if (options.urgency && options.urgency !== "all" && request.urgency !== options.urgency) return false;
    return true;
  });
}

export function requestPracticeNames(request: CooperationRequest, practiceAreas: PracticeArea[]) {
  return practiceAreas.filter((area) => request.practiceAreaIds.includes(area.id)).map((area) => area.name);
}

export function rankCooperationTargets(
  data: LexData,
  input: {
    requestType: CooperationRequestType;
    jurisdiction?: string;
    practiceAreaIds: string[];
    targetFirmId?: string;
  },
) {
  return data.firms
    .filter((firm) => firm.id !== data.viewerFirm?.id)
    .filter((firm) => firm.status === "active")
    .filter((firm) => canReceiveCooperationRequest(firm, input.requestType))
    .map((firm) => {
      const firmPracticeIds = new Set(data.firmPracticeAreas.filter((link) => link.firmId === firm.id).map((link) => link.practiceAreaId));
      const representativeIds = data.users.filter((user) => user.firmId === firm.id && user.active).map((user) => user.id);
      const lawyerPracticeIds = new Set(data.userPracticeAreas.filter((link) => representativeIds.includes(link.userId)).map((link) => link.practiceAreaId));
      const matchedPracticeIds = input.practiceAreaIds.filter((id) => firmPracticeIds.has(id) || lawyerPracticeIds.has(id));
      const reasons: string[] = [];
      let score = 0;

      if (input.targetFirmId === firm.id) {
        score += 2;
        reasons.push("currently selected");
      }
      if (input.jurisdiction && firm.jurisdiction === input.jurisdiction) {
        score += 6;
        reasons.push(firm.jurisdiction);
      }
      if (matchedPracticeIds.length > 0) {
        score += matchedPracticeIds.length * 4;
        reasons.push(...data.practiceAreas.filter((area) => matchedPracticeIds.includes(area.id)).map((area) => area.name));
      }
      if (firm.cooperationInterests?.includes(input.requestType)) {
        score += 4;
        reasons.push(labelForRequestType(input.requestType));
      }
      if (firm.acceptsCooperationRequests !== false) score += 1;
      if (firm.responseTimeIndicator) {
        score += 1;
        reasons.push(firm.responseTimeIndicator);
      }

      return { firm, score, reasons: [...new Set(reasons)].slice(0, 4) };
    })
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score || left.firm.name.localeCompare(right.firm.name))
    .slice(0, 5);
}

export function cooperationRequestMutationArgs(request: CooperationRequest) {
  return {
    requestType: request.requestType,
    title: request.title,
    shortSummary: request.shortSummary,
    jurisdictionTags: request.jurisdictionTags,
    practiceAreaIds: request.practiceAreaIds,
    urgency: request.urgency,
    confidentialityLevel: request.confidentialityLevel,
    targetFirmId: request.targetFirmId,
    targetUserId: request.targetUserId ?? null,
    relatedMemberId: request.relatedMemberId ?? null,
    conflictCheckRequired: request.conflictCheckRequired,
    allowFirmRouting: request.allowFirmRouting,
    deadline: request.deadline ?? null,
  };
}
